import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listUccData, exportUccDataExcel } from '../services/project750kService';

export default function UccDataViewer() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 500, // Items per page (increased for better performance)
    totalItems: 0,
    totalPages: 0
  });
  const [exporting, setExporting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listUccData({
        search: searchTerm,
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      setData(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Error fetching UCC data:', error);
      alert('Failed to load UCC data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle export (current page only)
  const handleExport = async () => {
    try {
      setExporting(true);
      // Export only the currently visible page
      await exportUccDataExcel(searchTerm, pagination.page, pagination.pageSize);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle sorting (client-side, within visible results)
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null; // Reset to original order
    }
    setSortConfig({ key, direction });
  };

  // Sort data based on sortConfig
  const getSortedData = () => {
    if (!sortConfig.key || !sortConfig.direction) {
      return data; // Return original order
    }

    const sortedData = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      // Convert to string for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedData;
  };

  const displayData = getSortedData();

  // Styles (LinkedIn Blue 2025 Theme)
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#F3F6F8',
      padding: '20px'
    },
    header: {
      backgroundColor: '#0077B5',
      color: '#FFFFFF',
      padding: '20px',
      borderRadius: '8px 8px 0 0',
      borderBottom: '1px solid #006097',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      margin: 0
    },
    backButton: {
      backgroundColor: 'transparent',
      color: '#FFFFFF',
      border: '2px solid #FFFFFF',
      borderRadius: '8px',
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    content: {
      backgroundColor: '#FFFFFF',
      borderRadius: '0 0 8px 8px',
      border: '2px solid #0077B5',
      borderTop: 'none'
    },
    toolbar: {
      padding: '20px',
      borderBottom: '1px solid #DEE2E6',
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    searchInput: {
      flex: '1',
      minWidth: '200px',
      backgroundColor: '#FFFFFF',
      color: '#2C3E50',
      border: '1px solid #D0D7DE',
      borderRadius: '4px',
      padding: '8px',
      fontSize: '14px',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    searchButton: {
      backgroundColor: '#0077B5',
      color: '#FFFFFF',
      border: '2px solid #0077B5',
      borderRadius: '8px',
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    exportButton: {
      backgroundColor: '#27AE60',
      color: '#FFFFFF',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    tableWrapper: {
      padding: '20px',
      overflowX: 'auto'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    th: {
      backgroundColor: '#0077B5',
      color: '#FFFFFF',
      padding: '12px 8px',
      textAlign: 'left',
      fontWeight: 'bold',
      fontSize: '12px',
      borderBottom: '2px solid #006097'
    },
    td: {
      padding: '8px',
      borderBottom: '1px solid #F0F0F0',
      color: '#2C3E50'
    },
    trEven: {
      backgroundColor: '#F9FAFB'
    },
    trHover: {
      backgroundColor: '#E8F4FD'
    },
    pagination: {
      padding: '20px',
      borderTop: '1px solid #DEE2E6',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    paginationInfo: {
      fontSize: '14px',
      color: '#666666'
    },
    paginationControls: {
      display: 'flex',
      gap: '8px'
    },
    pageButton: {
      backgroundColor: '#F9FAFB',
      color: '#0077B5',
      border: '2px solid #0077B5',
      borderRadius: '8px',
      padding: '6px 12px',
      fontSize: '14px',
      fontWeight: 'bold',
      cursor: 'pointer',
      fontFamily: 'Segoe UI, Arial, sans-serif'
    },
    pageButtonDisabled: {
      backgroundColor: '#CCCCCC',
      color: '#999999',
      border: '2px solid #CCCCCC',
      cursor: 'not-allowed'
    },
    loading: {
      padding: '40px',
      textAlign: 'center',
      fontSize: '14px',
      color: '#666666'
    }
  };

  return (
    <div style={styles.container}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>UCCMstrLB4 Data</h1>
          <button
            style={styles.backButton}
            onClick={() => navigate('/')}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        <div style={styles.content}>
          {/* Info Banner - Row 1 */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              backgroundColor: '#E7F8FF', 
              border: '1px solid #17A2B8', 
              borderRadius: '4px', 
              padding: '10px 15px',
              fontSize: '13px',
              color: '#0C5460',
              fontFamily: 'Segoe UI, Arial, sans-serif'
            }}>
              ℹ️ <strong>Note:</strong> Displaying first 25,000 records from DynamoDB for optimal performance. Full dataset contains 56,821 records. Use search to find specific entries.
            </div>
          </div>

          {/* Search & Export Controls - Row 2 */}
          <div style={styles.toolbar}>
            <input
              type="text"
              placeholder="Search by debtor name, ID, county, secured party, or record ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              style={styles.searchInput}
            />
            <button
              style={styles.searchButton}
              onClick={handleSearch}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#005885';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#0077B5';
              }}
            >
              Search
            </button>
            <button
              style={styles.exportButton}
              onClick={handleExport}
              disabled={exporting}
              onMouseEnter={(e) => {
                if (!exporting) e.target.style.backgroundColor = '#229954';
              }}
              onMouseLeave={(e) => {
                if (!exporting) e.target.style.backgroundColor = '#27AE60';
              }}
              title={`Export current page (up to ${pagination.pageSize} records)`}
            >
              {exporting ? 'Exporting...' : `📥 Export Current Page`}
            </button>
          </div>

          {/* Table */}
          <div style={styles.tableWrapper}>
            {loading ? (
              <div style={styles.loading}>Loading data...</div>
            ) : data.length === 0 ? (
              <div style={styles.loading}>No data found</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {[
                      { key: 'farmProduct', label: 'Farm Product' },
                      { key: 'cropYears', label: 'Crop Year(s)' },
                      { key: 'debtorName', label: 'Debtor Name' },
                      { key: 'debtorId', label: 'Debtor ID #' },
                      { key: 'counties', label: 'County(ies)' },
                      { key: 'debtorAddress', label: 'Debtor Address' },
                      { key: 'additionalDebtors', label: 'Additional Debtors' },
                      { key: 'securedParties', label: 'Secured Party(ies)' },
                      { key: 'assignees', label: 'Assignee(s)' },
                      { key: 'additionalFarmProducts', label: 'Additional Farm Product(s)' },
                      { key: 'description', label: 'Description' },
                      { key: 'originalRecordId', label: 'Record Id #' },
                      { key: 'amendmentIds', label: 'Amendment ID #(s)' }
                    ].map(({ key, label }) => (
                      <th 
                        key={key}
                        style={{
                          ...styles.th,
                          cursor: 'pointer',
                          userSelect: 'none',
                          position: 'relative'
                        }}
                        onClick={() => handleSort(key)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#005885';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#0077B5';
                        }}
                        title={`Click to sort by ${label}`}
                      >
                        {label}
                        {sortConfig.key === key && (
                          <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                            {sortConfig.direction === 'asc' ? '▲' : sortConfig.direction === 'desc' ? '▼' : ''}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayData.map((row, index) => (
                    <tr
                      key={row.recordId || index}
                      style={index % 2 === 0 ? {} : styles.trEven}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#E8F4FD';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#FFFFFF' : '#F9FAFB';
                      }}
                    >
                      <td style={styles.td}>{row.farmProduct || ''}</td>
                      <td style={styles.td}>{row.cropYears || ''}</td>
                      <td style={styles.td}>{row.debtorName || ''}</td>
                      <td style={styles.td}>{row.debtorId || ''}</td>
                      <td style={styles.td}>{row.counties || ''}</td>
                      <td style={styles.td}>{row.debtorAddress || ''}</td>
                      <td style={styles.td}>{row.additionalDebtors || ''}</td>
                      <td style={styles.td}>{row.securedParties || ''}</td>
                      <td style={styles.td}>{row.assignees || ''}</td>
                      <td style={styles.td}>{row.additionalFarmProducts || ''}</td>
                      <td style={styles.td}>{row.description || ''}</td>
                      <td style={styles.td}>{row.originalRecordId || row.recordId || ''}</td>
                      <td style={styles.td}>{row.amendmentIds || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && data.length > 0 && (
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of{' '}
                {pagination.totalItems} results
              </div>
              <div style={styles.paginationControls}>
                <button
                  style={{
                    ...styles.pageButton,
                    ...(pagination.page === 1 ? styles.pageButtonDisabled : {})
                  }}
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                >
                  First
                </button>
                <button
                  style={{
                    ...styles.pageButton,
                    ...(pagination.page === 1 ? styles.pageButtonDisabled : {})
                  }}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                <span style={{ padding: '6px 12px', fontSize: '14px', color: '#2C3E50' }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  style={{
                    ...styles.pageButton,
                    ...(pagination.page === pagination.totalPages ? styles.pageButtonDisabled : {})
                  }}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
                <button
                  style={{
                    ...styles.pageButton,
                    ...(pagination.page === pagination.totalPages ? styles.pageButtonDisabled : {})
                  }}
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

